import { Language } from '@contracts/client/Language';
import { State } from '@contracts/order/valueNormalizers/State';
import { DoingBusinessAsField } from '@src/permitField/CompanyInformationSectionFields';
import { optionalJurisdictionIdsFields } from '@src/permitField/JurisdictionIdNumberFields';
import {
  PrimaryDeliveryTypeField,
  PrimaryDeliveryTypeValueField,
  SecondaryDeliveryTypeField,
  SecondaryDeliveryTypeValueField,
} from '@src/permitField/PermitDeliveryAndNotificationsFields';
import * as yup from 'yup';

export const ClientFormSchema = yup.object().shape({
  name: yup.string().trim().required(),
  [DoingBusinessAsField.fieldName]: DoingBusinessAsField.validator,
  communicationLanguage: yup
    .string()
    .trim()
    .required()
    .oneOf(Object.values(Language)),
  fax: yup.string().trim().optional(),
  primaryContact: yup
    .object()
    .shape({
      name: yup.string().trim().required(),
      phone: yup.string().trim().required(),
      email: yup.string().trim().email().required(),
    })
    .required(),
  address: yup
    .object()
    .shape({
      address: yup.string().trim().required(),
      addressDetails: yup.string().trim().notRequired(),
      city: yup.string().trim().required(),
      state: yup.string().trim().required().oneOf(Object.values(State)),
      zipCode: yup.string().trim().required(),
    })
    .required(),
  mailingAddress: yup
    .object({
      address: yup.string().trim().required(),
      addressDetails: yup.string().trim().notRequired(),
      city: yup.string().trim().required(),
      state: yup.string().trim().required().oneOf(Object.values(State)),
      zipCode: yup.string().trim().required(),
    })
    .when('$isMailingAddressSameAsPhysical', {
      is: true,
      then: (schema) =>
        schema
          .shape({
            address: yup.string().trim().notRequired(),
            addressDetails: yup.string().trim().notRequired(),
            city: yup.string().trim().notRequired(),
            state: yup
              .string()
              .trim()
              .oneOf(Object.values(State))
              .notRequired(),
            zipCode: yup.string().trim().notRequired(),
          })
          .optional()
          .default(undefined),
    }),
  primaryDeliveryType: yup
    .object()
    .shape({
      primaryDeliveryType: PrimaryDeliveryTypeField.validator,
      value: PrimaryDeliveryTypeValueField.validator,
    })
    .required(),
  secondaryDeliveryType: yup.object().shape({
    secondaryDeliveryType: SecondaryDeliveryTypeField.validator,
    value: SecondaryDeliveryTypeValueField.validator,
  }),
  permitNotifications: yup.object().shape({
    email: yup.string().trim().email().notRequired(),
    phone: yup.string().trim().notRequired(),
  }),
  insuranceInformation: yup.object().shape({
    insuranceCompany: yup.string().trim().notRequired(),
    insuranceAddressNumber: yup.string().trim().notRequired(),
    insuranceEffectiveDate: yup.string().trim().notRequired(),
    insuranceExperienceDate: yup.string().trim().notRequired(),
    insurancePolicyNumber: yup.string().trim().notRequired(),
    insuranceCoverageAmount: yup.string().trim().notRequired(),
    insuranceAgentAmount: yup.string().trim().notRequired(),
    insurancePhoneNumber: yup.string().trim().notRequired(),
    insuranceIsOnFileWithState: yup.boolean().notRequired(),
  }),
  jurisdictionIdNumbers: yup.object().shape(optionalJurisdictionIdsFields),
});

export type ClientFormSchema = yup.InferType<typeof ClientFormSchema>;

export const hasInsuranceInformation = (data: ClientFormSchema) =>
  !!(
    data.insuranceInformation?.insuranceCompany ||
    data.insuranceInformation?.insuranceAddressNumber ||
    data.insuranceInformation?.insuranceEffectiveDate ||
    data.insuranceInformation?.insuranceExperienceDate ||
    data.insuranceInformation?.insurancePolicyNumber ||
    data.insuranceInformation?.insuranceCoverageAmount ||
    data.insuranceInformation?.insuranceAgentAmount ||
    data.insuranceInformation?.insurancePhoneNumber ||
    data.insuranceInformation?.insuranceIsOnFileWithState
  );

export const mapJurisdictionIdNumbersToClientForm = (
  jurisdictionIdNumbers: { field: string; value: string }[],
) => {
  return jurisdictionIdNumbers.reduce(
    (acc, { field, value }) => {
      acc[field] = value;
      return acc;
    },
    {} as Record<string, string>,
  );
};
